import { flushLogs, logUserReport } from "@lib/logger";

type QueuedReport = {
	id: string;
	message: string;
	details: Record<string, unknown>;
};

const STORAGE_KEY = 'bigbash_queued_reports';
const FLUSH_TIMEOUT_MS = 5_000;
const RETRY_INTERVAL_MS = 30_000;

let isFlushing = false;
let retryInterval: ReturnType<typeof setInterval> | null = null;

function readQueue(): QueuedReport[] {
	const storedQueue = localStorage.getItem(STORAGE_KEY);
	return storedQueue ? JSON.parse(storedQueue) as QueuedReport[] : [];
}

function writeQueue(queue: QueuedReport[]): void {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

export async function enqueueReport(message: string, details: Record<string, unknown>): Promise<void> {
	const report: QueuedReport = { id: crypto.randomUUID(), message, details };
	writeQueue([...readQueue(), report]);
	await flushQueuedReports();
}

export async function flushQueuedReports(): Promise<void> {
	if (isFlushing) {
		return;
	}
	isFlushing = true;
	try {
		const queue = readQueue();
		const remainingReports: QueuedReport[] = [];
		for (const report of queue) {
			logUserReport(report.message, report.details);
			const wasDelivered = await flushLogs(FLUSH_TIMEOUT_MS);
			if (!wasDelivered) {
				remainingReports.push(report);
			}
		}
		writeQueue(remainingReports);
	} finally {
		isFlushing = false;
	}
}

const handleOnline = (): void => void flushQueuedReports();

export function startReportQueueRetries(): void {
	window.addEventListener('online', handleOnline);
	retryInterval = setInterval((): void => void flushQueuedReports(), RETRY_INTERVAL_MS);
	void flushQueuedReports();
}

export function stopReportQueueRetries(): void {
	window.removeEventListener('online', handleOnline);
	if (retryInterval !== null) {
		clearInterval(retryInterval);
		retryInterval = null;
	}
}
